/**
 * Drop zone for assigning tutors and students (unified or matching mode).
 */

import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Users, AlertCircle, GraduationCap, User, Heart, ShieldCheck, UserX } from 'lucide-react';
import { RemovableParticipantChip } from '@/components/common/RemovableParticipantChip';
import { TutorChipWithMatches } from './TutorChipWithMatches';
import { cn } from '@/lib/utils';
import type { UserWithProfile } from '@/types/user.types';
import type { UnregisteredStudentData } from '@/types/event.types';
import type { SlotAvailabilityById } from '@/types/slot-availability.types';
import { formatConflictHint } from '@/components/pairs/inperson/matching-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import type { ParticipantAttendanceById } from '@/types/attendance.types';

export type { ParticipantAttendanceById };

/** `unified` = one pool; `matching` = tutors hold matched students. */
type DropZoneMode = 'unified' | 'matching';
/** Drag payload kind used for capacity / already-assigned checks. */
type ActiveDragType = 'tutor' | 'student';

/** Props for {@link UnifiedDropZone}. */
interface UnifiedDropZoneProps {
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  maxTutors?: number | null;
  maxStudents?: number | null;
  onRemoveTutor: (userId: string) => void;
  onRemoveStudent: (userId: string) => void;
  className?: string;
  sessionId?: string;
  specificMatches?: Array<{ studentId: string; tutorId: string }>;
  getTutorMatchedStudents?: (
    tutorId: string,
  ) => Array<UserWithProfile & { assignmentSource?: string }>;
  onUnmatchStudent?: (studentId: string, tutorId: string) => void;
  mode?: DropZoneMode;
  studentsOverCapacity?: number;
  tutorsOverCapacity?: number;
  /**
   * Hide in-chip matching counts (tutor-section "N matchings" badge and per-tutor count).
   * Use when the parent already shows that total.
   */
  hideTutorMatchCount?: boolean;
  /** Tutor availability on the current slot (inline on chips). */
  tutorAvailabilityById?: SlotAvailabilityById;
  /** Student availability on the current slot (inline on chips). */
  studentAvailabilityById?: SlotAvailabilityById;
  /** Attendance tag per participant (same as session details). */
  attendanceByUserId?: ParticipantAttendanceById;
  /** Custom label for the tutor/organizer section. */
  tutorSectionLabel?: string;
  /**
   * When true, group tutors/organizers by currentRole into per-role subsections
   * (tutors, volunteers, coordinators).
   */
  tutorGroupByRole?: boolean;
  /** Unregistered (non-platform) students to display in the students column */
  unregisteredStudents?: UnregisteredStudentData[];
  /** Called when user removes an unregistered student by index */
  onRemoveUnregistered?: (index: number) => void;
  /** Called when user clicks the edit button on an unregistered student chip */
  onEditUnregistered?: (index: number) => void;
}

/** Shared args for matching chips vs plain removable chips. */
interface RenderTutorChipsParams {
  tutors: UserWithProfile[];
  mode: DropZoneMode;
  sessionId?: string;
  getTutorMatchedStudents?: (
    tutorId: string,
  ) => Array<UserWithProfile & { assignmentSource?: string }>;
  onUnmatchStudent?: (studentId: string, tutorId: string) => void;
  hideTutorMatchCount?: boolean;
  tutorAvailabilityById?: SlotAvailabilityById;
  studentAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
  onRemoveTutor: (userId: string) => void;
}

/** Accepted drag types / role groups for student vs tutor-side drops. */
const STUDENT_DRAG_TYPES = new Set(['student']);
/** Role groups accepted on the student drop side. */
const STUDENT_ROLE_GROUPS = new Set(['students']);
/** Drag payload types accepted on the tutor drop side. */
const TUTOR_DRAG_TYPES = new Set(['tutor', 'volunteer', 'coordinator']);
/** Role groups accepted on the tutor drop side. */
const TUTOR_ROLE_GROUPS = new Set(['tutors', 'volunteers', 'admins']);

/** Matching mode hides students already paired with a tutor. */
function getUnmatchedStudents(
  mode: DropZoneMode,
  students: UserWithProfile[],
  specificMatches: Array<{ studentId: string; tutorId: string }>,
): UserWithProfile[] {
  if (mode !== 'matching') return students;
  const matchedIds = new Set(specificMatches.map((m) => m.studentId));
  return students.filter((s) => !matchedIds.has(s.id));
}

/** Droppable id/data: matching is session-scoped; unified accepts any participant. */
function getDroppableConfig(mode: DropZoneMode, sessionId?: string) {
  if (mode === 'matching') {
    return {
      id: sessionId ? `matching-dropzone-unified-${sessionId}` : 'unified-participants-drop',
      data: { type: 'matching-dropzone-unified', sessionId },
    };
  }
  return {
    id: 'unified-participants-drop',
    data: { acceptsAll: true },
  };
}

/** Map dnd `type` / `roleGroup` to tutor vs student (volunteers count as tutors). */
function resolveActiveDragType(rawType?: string, roleGroup?: string): ActiveDragType | undefined {
  if (STUDENT_DRAG_TYPES.has(rawType ?? '') || STUDENT_ROLE_GROUPS.has(roleGroup ?? '')) {
    return 'student';
  }
  if (TUTOR_DRAG_TYPES.has(rawType ?? '') || TUTOR_ROLE_GROUPS.has(roleGroup ?? '')) {
    return 'tutor';
  }
  return undefined;
}

/** True when the dragged user is already in the matching column list. */
function isUserAlreadyAssigned(
  user: UserWithProfile | undefined,
  dragType: ActiveDragType | undefined,
  tutors: UserWithProfile[],
  students: UserWithProfile[],
): boolean {
  if (!user || !dragType) return false;
  const list = dragType === 'tutor' ? tutors : students;
  return list.some((participant) => participant.id === user.id);
}

/** `max == null` means no cap. */
function isCapacityReached(max: number | null | undefined, current: number): boolean {
  return max != null && current >= max;
}

/** Capacity overlay only for a new drop of the type that is already at max. */
function shouldWarnCapacity(
  isOver: boolean,
  alreadyInSession: boolean,
  dragType: ActiveDragType | undefined,
  tutorsAtCap: boolean,
  studentsAtCap: boolean,
): boolean {
  if (!isOver || alreadyInSession) return false;
  if (dragType === 'tutor') return tutorsAtCap;
  if (dragType === 'student') return studentsAtCap;
  return false;
}

/** Empty / filled / drop-over / at-capacity chrome. */
function getDropZoneClassName(
  isEmpty: boolean,
  shouldShowDropOver: boolean,
  showCapacityWarning: boolean,
  className?: string,
): string {
  return cn(
    'droppable-zone rounded-xl transition-all duration-300 min-h-[465px] relative',
    isEmpty && 'droppable-zone-empty',
    !isEmpty && 'droppable-zone-filled',
    shouldShowDropOver && 'drop-over',
    showCapacityWarning && 'drop-capacity-reached',
    className,
  );
}

/** Adapter so unregistered event guests reuse {@link RemovableParticipantChip}. */
function toSyntheticUnregisteredUser(
  participant: UnregisteredStudentData,
  index: number,
): UserWithProfile {
  return {
    id: `unreg-${index}`,
    companyId: '',
    firebaseUid: '',
    email: participant.primaryContactType === 'email' ? (participant.primaryContact ?? null) : null,
    firstName: participant.firstName,
    lastName: participant.lastName,
    displayName: `${participant.firstName} ${participant.lastName}`.trim(),
    photoUrl: null,
    phone: participant.primaryContactType === 'phone' ? (participant.primaryContact ?? null) : null,
    status: 'active',
    createdAt: new Date(),
    updatedAt: new Date(),
    currentRole: 'student',
  };
}

/** Matching + sessionId → {@link TutorChipWithMatches}; otherwise a removable chip. */
function renderTutorChips({
  tutors,
  mode,
  sessionId,
  getTutorMatchedStudents,
  onUnmatchStudent,
  hideTutorMatchCount,
  tutorAvailabilityById,
  studentAvailabilityById,
  attendanceByUserId,
  onRemoveTutor,
}: RenderTutorChipsParams) {
  if (mode === 'matching' && sessionId) {
    return tutors.map((user) => {
      const matchedStudents = getTutorMatchedStudents?.(user.id) || [];
      const availability = tutorAvailabilityById?.[user.id];
      return (
        <TutorChipWithMatches
          key={user.id}
          tutor={user}
          matchedStudents={matchedStudents}
          onRemove={() => onRemoveTutor(user.id)}
          sessionId={sessionId}
          onStudentRemove={(studentId) => onUnmatchStudent?.(studentId, user.id)}
          hideMatchCountBadge={hideTutorMatchCount}
          slotAvailability={availability?.status}
          slotConflictHint={formatConflictHint(availability?.conflictingSession)}
          studentAvailabilityById={studentAvailabilityById}
          attendanceByUserId={attendanceByUserId}
        />
      );
    });
  }
  return tutors.map((user) => {
    const availability = tutorAvailabilityById?.[user.id];
    const attendance = attendanceByUserId?.[user.id];
    return (
      <RemovableParticipantChip
        key={user.id}
        user={user}
        role={user.currentRole === 'volunteer' ? 'volunteer' : 'tutor'}
        hideRoleLabel
        onRemove={() => onRemoveTutor(user.id)}
        slotAvailability={availability?.status}
        slotConflictHint={formatConflictHint(availability?.conflictingSession)}
        attendanceStatus={attendance?.status}
        absenceJustification={attendance?.justification}
      />
    );
  });
}

/** Empty-state copy for an unused drop zone. */
function EmptyParticipantsState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12">
      <Users className="w-12 h-12 text-muted-foreground/40" />
      <div className="text-center">
        <p className="text-base font-medium text-[var(--color-1-nuance-1)] mb-1">
          Aucun participant
        </p>
        <p className="text-sm text-muted-foreground">
          Glissez-déposez des tuteurs et des élèves ici
        </p>
      </div>
    </div>
  );
}

/** Badge for the tutor-column matching total (hidden when the parent already shows it). */
function MatchingCountBadge({ totalMatches }: Readonly<{ totalMatches: number }>) {
  return (
    <span
      className="text-xs font-bold px-2 py-0.5 rounded-full border"
      style={{
        background: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
        color: 'var(--color-3-nuance-1)',
        borderColor: 'var(--color-3-nuance-1)',
      }}
    >
      {totalMatches} {totalMatches > 1 ? 'matchings' : 'matching'}
    </span>
  );
}

/** Left column: tutors/organizers, optional per-role grouping. */
function TutorColumn({
  tutors,
  mode,
  sessionId,
  getTutorMatchedStudents,
  onUnmatchStudent,
  hideTutorMatchCount,
  tutorAvailabilityById,
  studentAvailabilityById,
  attendanceByUserId,
  onRemoveTutor,
  tutorSectionLabel,
  tutorGroupByRole,
  specificMatches,
  tutorsCapacityReached,
  maxTutors,
}: Readonly<{
  tutors: UserWithProfile[];
  mode: DropZoneMode;
  sessionId?: string;
  getTutorMatchedStudents?: RenderTutorChipsParams['getTutorMatchedStudents'];
  onUnmatchStudent?: RenderTutorChipsParams['onUnmatchStudent'];
  hideTutorMatchCount?: boolean;
  tutorAvailabilityById?: SlotAvailabilityById;
  studentAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
  onRemoveTutor: (userId: string) => void;
  tutorSectionLabel: string;
  tutorGroupByRole: boolean;
  specificMatches: Array<{ studentId: string; tutorId: string }>;
  tutorsCapacityReached: boolean;
  maxTutors?: number | null;
}>) {
  const totalMatches = mode === 'matching' ? specificMatches.length : 0;
  const showMatchingBadge = mode === 'matching' && totalMatches > 0 && !hideTutorMatchCount;

  return (
    <div className="pr-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--color-1-nuance-3)]">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
          <h5 className="font-semibold text-sm text-[var(--color-2-nuance-1)] uppercase tracking-wide">
            {tutorSectionLabel}
          </h5>
          {showMatchingBadge && <MatchingCountBadge totalMatches={totalMatches} />}
        </div>
        <span
          className={cn(
            'capacity-indicator text-xs font-bold px-2.5 py-1 rounded-full',
            'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)]',
            'text-[var(--color-2-nuance-1)] border border-[var(--color-2-nuance-1)]',
            tutorsCapacityReached &&
              'capacity-indicator-at-limit bg-[rgb(var(--color-error)_/_0.15)] text-[rgb(var(--color-error))] border-[rgb(var(--color-error))]',
          )}
        >
          {tutors.length}/{maxTutors || '8'}
        </span>
      </div>
      {tutorGroupByRole ? (
        <TutorGroupedByRole
          tutors={tutors}
          onRemoveTutor={onRemoveTutor}
          tutorAvailabilityById={tutorAvailabilityById}
          attendanceByUserId={attendanceByUserId}
          emptyLabel={tutorSectionLabel}
        />
      ) : (
        <div className="flex flex-wrap gap-2 min-h-[80px]">
          {tutors.length > 0 ? (
            renderTutorChips({
              tutors,
              mode,
              sessionId,
              getTutorMatchedStudents,
              onUnmatchStudent,
              hideTutorMatchCount,
              tutorAvailabilityById,
              studentAvailabilityById,
              attendanceByUserId,
              onRemoveTutor,
            })
          ) : (
            <div className="flex items-center justify-center w-full py-6 text-xs text-muted-foreground">
              Glissez des {tutorSectionLabel.toLowerCase()} ici
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Platform students not already matched to a tutor (matching mode). */
function RegisteredStudentsList({
  unmatchedStudents,
  showSubsectionLabel,
  studentAvailabilityById,
  attendanceByUserId,
  onRemoveStudent,
  mode,
}: Readonly<{
  unmatchedStudents: UserWithProfile[];
  showSubsectionLabel: boolean;
  studentAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
  onRemoveStudent: (userId: string) => void;
  mode: DropZoneMode;
}>) {
  return (
    <div>
      {showSubsectionLabel && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <User className="w-3 h-3" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span
            className="text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-1-nuance-1)' }}
          >
            Inscrits
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {unmatchedStudents.map((user) => {
          const availability = studentAvailabilityById?.[user.id];
          const attendance = attendanceByUserId?.[user.id];
          return (
            <RemovableParticipantChip
              key={user.id}
              user={user}
              role="student"
              onRemove={() => onRemoveStudent(user.id)}
              draggable={mode === 'matching'}
              slotAvailability={availability?.status}
              slotConflictHint={formatConflictHint(availability?.conflictingSession)}
              attendanceStatus={attendance?.status}
              absenceJustification={attendance?.justification}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Event guests without a platform account (index-based remove/edit). */
function UnregisteredStudentsList({
  unregisteredStudents,
  onRemoveUnregistered,
  onEditUnregistered,
}: Readonly<{
  unregisteredStudents: UnregisteredStudentData[];
  onRemoveUnregistered?: (index: number) => void;
  onEditUnregistered?: (index: number) => void;
}>) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <UserX className="w-3 h-3" style={{ color: 'var(--color-3-nuance-1)' }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-wide"
          style={{ color: 'var(--color-3-nuance-1)' }}
        >
          Non inscrits
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {unregisteredStudents.map((participant, idx) => (
          <RemovableParticipantChip
            key={`unreg-${participant.firstName}-${participant.lastName}-${idx}`}
            user={toSyntheticUnregisteredUser(participant, idx)}
            role="student"
            draggable={false}
            onRemove={onRemoveUnregistered ? () => onRemoveUnregistered(idx) : () => {}}
            onEdit={onEditUnregistered ? () => onEditUnregistered(idx) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/** Right column: registered + unregistered students and capacity. */
function StudentColumn({
  unmatchedStudents,
  unregisteredStudents,
  studentsCapacityReached,
  maxStudents,
  studentAvailabilityById,
  attendanceByUserId,
  onRemoveStudent,
  onRemoveUnregistered,
  onEditUnregistered,
  mode,
}: Readonly<{
  unmatchedStudents: UserWithProfile[];
  unregisteredStudents: UnregisteredStudentData[];
  studentsCapacityReached: boolean;
  maxStudents?: number | null;
  studentAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
  onRemoveStudent: (userId: string) => void;
  onRemoveUnregistered?: (index: number) => void;
  onEditUnregistered?: (index: number) => void;
  mode: DropZoneMode;
}>) {
  const hasRegistered = unmatchedStudents.length > 0;
  const hasUnregistered = unregisteredStudents.length > 0;

  return (
    <div className="pl-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--color-1-nuance-3)]">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[var(--color-3-nuance-1)]" />
          <h5 className="font-semibold text-sm text-[var(--color-1-nuance-1)] uppercase tracking-wide">
            Élèves
          </h5>
        </div>
        <span
          className={cn(
            'capacity-indicator text-xs font-bold px-2.5 py-1 rounded-full',
            'bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]',
            'text-[var(--color-1-nuance-1)] border border-[var(--color-1-nuance-1)]',
            studentsCapacityReached &&
              'capacity-indicator-at-limit bg-[rgb(var(--color-error)_/_0.15)] text-[rgb(var(--color-error))] border-[rgb(var(--color-error))]',
          )}
        >
          {unmatchedStudents.length + unregisteredStudents.length}/{maxStudents || '8'}
        </span>
      </div>
      <div className="space-y-3 min-h-[80px]">
        {hasRegistered && (
          <RegisteredStudentsList
            unmatchedStudents={unmatchedStudents}
            showSubsectionLabel={hasUnregistered}
            studentAvailabilityById={studentAvailabilityById}
            attendanceByUserId={attendanceByUserId}
            onRemoveStudent={onRemoveStudent}
            mode={mode}
          />
        )}
        {hasUnregistered && (
          <UnregisteredStudentsList
            unregisteredStudents={unregisteredStudents}
            onRemoveUnregistered={onRemoveUnregistered}
            onEditUnregistered={onEditUnregistered}
          />
        )}
        {!hasRegistered && !hasUnregistered && (
          <div className="flex items-center justify-center w-full py-6 text-xs text-muted-foreground">
            Glissez des élèves ici
          </div>
        )}
      </div>
    </div>
  );
}

/** Two-column filled state with a centered vertical separator. */
function FilledParticipantsGrid(
  props: React.ComponentProps<typeof TutorColumn> & React.ComponentProps<typeof StudentColumn>,
) {
  return (
    <>
      {/* Full-height vertical separator, absolutely positioned in the droppable zone */}
      <div className="absolute inset-y-4 left-1/2 -translate-x-1/2 w-px bg-[var(--color-1-nuance-3)]" />
      <div className="grid grid-cols-2 gap-4">
        <TutorColumn {...props} />
        <StudentColumn {...props} />
      </div>
    </>
  );
}

/** Full-zone overlay when dropping a type that is already at max. */
function DropCapacityOverlay({
  activeDragType,
  maxTutors,
  maxStudents,
}: Readonly<{
  activeDragType?: ActiveDragType;
  maxTutors?: number | null;
  maxStudents?: number | null;
}>) {
  const message =
    activeDragType === 'tutor'
      ? getErrorMessage({ code: 'maxTutorsCapacityReached', max: maxTutors ?? 0 })
      : getErrorMessage({ code: 'maxStudentsCapacityReached', max: maxStudents ?? 0 });

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[rgb(var(--color-error)_/_0.12)] border-2 border-[rgb(var(--color-error))] rounded-xl pointer-events-none animate-shake z-10">
      <span className="text-sm font-bold text-[rgb(var(--color-error))] bg-white dark:bg-[rgb(var(--card-rgb))] px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        {message}
      </span>
    </div>
  );
}

/** Persistent banner when assigned counts already exceed the saved max. */
function OverCapacityBanner({
  studentsOverCapacity,
  tutorsOverCapacity,
  maxStudents,
  maxTutors,
}: Readonly<{
  studentsOverCapacity: number;
  tutorsOverCapacity: number;
  maxStudents?: number | null;
  maxTutors?: number | null;
}>) {
  if (studentsOverCapacity <= 0 && tutorsOverCapacity <= 0) return null;

  return (
    <div className="mt-3 p-3 bg-[rgb(var(--color-error)_/_0.08)] border border-[rgb(var(--color-error)_/_0.3)] rounded-lg">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-[rgb(var(--color-error))] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[rgb(var(--color-error))]">
          <p className="font-semibold mb-1">{getErrorMessage('capacityExceeded')}</p>
          {studentsOverCapacity > 0 && (
            <p>
              {getErrorMessage({
                code: 'removeStudentsOverCapacity',
                count: studentsOverCapacity,
                max: maxStudents ?? 0,
              })}
            </p>
          )}
          {tutorsOverCapacity > 0 && (
            <p>
              {getErrorMessage({
                code: 'removeTutorsOverCapacity',
                count: tutorsOverCapacity,
                max: maxTutors ?? 0,
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Drop zone for assigning tutors and students (unified or matching). */
export const UnifiedDropZone: React.FC<UnifiedDropZoneProps> = ({
  tutorSectionLabel = getRoleLabelPlural('tutor'),
  tutorGroupByRole = false,
  tutors,
  students,
  maxTutors,
  maxStudents,
  onRemoveTutor,
  onRemoveStudent,
  className,
  sessionId,
  specificMatches = [],
  getTutorMatchedStudents,
  onUnmatchStudent,
  mode = 'unified',
  studentsOverCapacity = 0,
  tutorsOverCapacity = 0,
  hideTutorMatchCount = false,
  tutorAvailabilityById,
  studentAvailabilityById,
  attendanceByUserId,
  unregisteredStudents = [],
  onRemoveUnregistered,
  onEditUnregistered,
}) => {
  const isEmpty = tutors.length === 0 && students.length === 0 && unregisteredStudents.length === 0;
  const unmatchedStudents = getUnmatchedStudents(mode, students, specificMatches);
  const { setNodeRef, isOver, active } = useDroppable(getDroppableConfig(mode, sessionId));

  const activeDragData = active?.data?.current;
  const activeDragType = resolveActiveDragType(
    activeDragData?.type as string | undefined,
    activeDragData?.roleGroup as string | undefined,
  );
  const activeDragUser = activeDragData?.user as UserWithProfile | undefined;

  /** Re-dropping an already-assigned user must not trigger the capacity overlay. */
  const isActiveDragUserAlreadyInSession = React.useMemo(
    () => isUserAlreadyAssigned(activeDragUser, activeDragType, tutors, students),
    [activeDragUser, activeDragType, tutors, students],
  );

  const tutorsCapacityReached = isCapacityReached(maxTutors, tutors.length);
  const studentsCapacityReached = isCapacityReached(maxStudents, students.length);
  /** Overlay vs highlight: at-capacity new drop vs a valid drop-over. */
  const showCapacityWarning = shouldWarnCapacity(
    isOver,
    isActiveDragUserAlreadyInSession,
    activeDragType,
    tutorsCapacityReached,
    studentsCapacityReached,
  );
  const shouldShowDropOver = isOver && !showCapacityWarning;

  return (
    <div
      ref={setNodeRef}
      className={getDropZoneClassName(isEmpty, shouldShowDropOver, showCapacityWarning, className)}
    >
      {isEmpty ? (
        <EmptyParticipantsState />
      ) : (
        <FilledParticipantsGrid
          tutors={tutors}
          mode={mode}
          sessionId={sessionId}
          getTutorMatchedStudents={getTutorMatchedStudents}
          onUnmatchStudent={onUnmatchStudent}
          hideTutorMatchCount={hideTutorMatchCount}
          tutorAvailabilityById={tutorAvailabilityById}
          studentAvailabilityById={studentAvailabilityById}
          attendanceByUserId={attendanceByUserId}
          onRemoveTutor={onRemoveTutor}
          tutorSectionLabel={tutorSectionLabel}
          tutorGroupByRole={tutorGroupByRole}
          specificMatches={specificMatches}
          tutorsCapacityReached={tutorsCapacityReached}
          maxTutors={maxTutors}
          unmatchedStudents={unmatchedStudents}
          unregisteredStudents={unregisteredStudents}
          studentsCapacityReached={studentsCapacityReached}
          maxStudents={maxStudents}
          onRemoveStudent={onRemoveStudent}
          onRemoveUnregistered={onRemoveUnregistered}
          onEditUnregistered={onEditUnregistered}
        />
      )}

      {showCapacityWarning && (
        <DropCapacityOverlay
          activeDragType={activeDragType}
          maxTutors={maxTutors}
          maxStudents={maxStudents}
        />
      )}

      <OverCapacityBanner
        studentsOverCapacity={studentsOverCapacity}
        tutorsOverCapacity={tutorsOverCapacity}
        maxStudents={maxStudents}
        maxTutors={maxTutors}
      />
    </div>
  );
};

/** Color/icon per currentRole group — not per specific role code */
const GROUP_STYLE: Record<string, { color: string; Icon: typeof GraduationCap }> = {
  tutor: { color: 'var(--color-2-nuance-1)', Icon: GraduationCap },
  volunteer: { color: 'var(--color-3-nuance-1)', Icon: Heart },
  coordinator: { color: 'var(--color-4-nuance-1)', Icon: ShieldCheck },
};

/** One currentRole bucket when `tutorGroupByRole` is on. */
interface OrganizerGroup {
  /** Role label from DB (role.label) */
  label: string;
  /** currentRole used to pick color/icon */
  groupKey: string;
  users: UserWithProfile[];
}

/** Props for {@link TutorGroupedByRole}. */
interface TutorGroupedByRoleProps {
  tutors: UserWithProfile[];
  onRemoveTutor: (userId: string) => void;
  tutorAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
  emptyLabel?: string;
}

/** Tutors/volunteers/admins list grouped by role for the drop zone. */
const TutorGroupedByRole: React.FC<TutorGroupedByRoleProps> = ({
  tutors,
  onRemoveTutor,
  tutorAvailabilityById,
  attendanceByUserId,
  emptyLabel = 'organisateurs',
}) => {
  if (tutors.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-6 text-xs text-muted-foreground">
        Glissez des {emptyLabel.toLowerCase()} ici
      </div>
    );
  }

  // Group by currentRole first so multi-role users stay in a stable section.
  // Label comes from the matching userRoles entry (no hardcoding).
  // styleKey (currentRole) drives color/icon only.
  /** Group by role code; `currentRole` only picks color/icon. */
  const groupMap = new Map<string, OrganizerGroup>();
  for (const user of tutors) {
    const roleCode = user.currentRole ?? user.userRoles?.[0]?.role?.code ?? 'tutor';
    const roleLabel =
      user.userRoles?.find((ur) => ur.role?.code === roleCode)?.role?.label ?? roleCode;
    const styleKey = user.currentRole ?? 'tutor';
    if (!groupMap.has(roleCode)) {
      groupMap.set(roleCode, { label: roleLabel, groupKey: styleKey, users: [] });
    }
    groupMap.get(roleCode)!.users.push(user);
  }

  // Order: tutors first, then volunteers, then coordinators
  /** Stable visual order: tutors, then volunteers, then coordinators. */
  const groupOrder = (key: string) => {
    const g = groupMap.get(key)?.groupKey ?? '';
    if (g === 'tutor') return 0;
    if (g === 'volunteer') return 1;
    return 2;
  };
  const orderedGroups = [...groupMap.entries()].sort(([a], [b]) => groupOrder(a) - groupOrder(b));

  return (
    <div className="space-y-3">
      {orderedGroups.map(([roleCode, { label, groupKey, users }]) => {
        const style = GROUP_STYLE[groupKey] ?? GROUP_STYLE.tutor;
        const RoleIcon = style.Icon;
        return (
          <div key={roleCode}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <RoleIcon className="w-3 h-3" style={{ color: style.color }} />
              <span
                className="text-[10px] font-semibold uppercase tracking-wide"
                style={{ color: style.color }}
              >
                {label}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {users.map((user) => {
                const availability = tutorAvailabilityById?.[user.id];
                const attendance = attendanceByUserId?.[user.id];
                return (
                  <RemovableParticipantChip
                    key={user.id}
                    user={user}
                    role={groupKey === 'volunteer' ? 'volunteer' : 'tutor'}
                    hideRoleLabel
                    onRemove={() => onRemoveTutor(user.id)}
                    slotAvailability={availability?.status}
                    slotConflictHint={formatConflictHint(availability?.conflictingSession)}
                    attendanceStatus={attendance?.status}
                    absenceJustification={attendance?.justification}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
